import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useAuth, API } from '../App';
import { toast } from 'sonner';
import axios from 'axios';
import Logo from '../components/Logo';
import {
  Calculator,
  Home,
  Euro,
  Percent,
  Calendar,
  FileText,
  Download,
  Save,
  ChevronLeft,
  TrendingUp,
  TrendingDown,
  Info,
  Loader2,
  Building2,
  Sofa,
  CreditCard,
  PiggyBank,
  Receipt,
  Shield,
  BarChart3,
  Scale,
  BookOpen,
  CheckCircle2,
  Droplets,
  Zap,
  Flame,
  Wifi,
  Wrench,
  Users,
  ShieldCheck,
  Trash2
} from 'lucide-react';

const CalculatorPage = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [results, setResults] = useState(null);
  const [activeTab, setActiveTab] = useState('calculator');
  const [savedCalcs, setSavedCalcs] = useState([]);

  // Input state - Organisation comme le site de référence
  const [inputs, setInputs] = useState({
    // Nom du projet
    project_name: '',
    
    // Type de location
    type_location: 'meuble',
    
    // 1. Investissement
    prix_achat: 200000,
    frais_notaire: 15000,
    travaux: 20000,
    mobilier: 0,
    
    // Frais de financement optionnels
    frais_courtage: 0,
    frais_garantie: 0,
    frais_dossier: 0,
    
    // 2. Financement
    montant_emprunt: 180000,
    taux_interet: 3.5,
    taux_assurance: 0.36,
    duree_pret: 20,
    
    // 3. Revenus
    loyer_mensuel: 900,
    
    // 4. Charges mensuelles
    charges_copro_mois: 100,
    taxe_fonciere_mois: 100,
    assurance_pno_mois: 30,
    
    // Services et fluides optionnels
    eau_mois: 0,
    electricite_mois: 0,
    gaz_mois: 0,
    internet_mois: 0,
    entretien_chaudiere_mois: 0,
    
    // Gestion et garanties optionnels
    gestion_locative_pct: 0,
    gli_pct: 0,
    
    // 5. Fiscalité
    regime_fiscal: 'lmnp',
    tmi: 30
  });

  const handleInputChange = (field, value) => {
    setInputs(prev => ({ ...prev, [field]: value }));
  };

  // Select all text on focus so "0" gets replaced immediately when typing
  const handleFocus = (e) => {
    e.target.select();
  };

  // Display empty string instead of 0 for cleaner UX
  const displayValue = (val) => (val === 0 ? '' : val);

  // Calculs automatiques
  const totalInvestissement = inputs.prix_achat + inputs.frais_notaire + inputs.travaux + inputs.mobilier;
  const totalFraisFinancement = inputs.frais_courtage + inputs.frais_garantie + inputs.frais_dossier;
  const apportPersonnel = totalInvestissement + totalFraisFinancement - inputs.montant_emprunt;
  
  // Calcul mensualité crédit
  const tauxMensuel = inputs.taux_interet / 100 / 12;
  const nbMensualites = inputs.duree_pret * 12;
  const mensualiteCredit = inputs.montant_emprunt > 0 && tauxMensuel > 0
    ? (inputs.montant_emprunt * tauxMensuel * Math.pow(1 + tauxMensuel, nbMensualites)) / (Math.pow(1 + tauxMensuel, nbMensualites) - 1)
    : inputs.montant_emprunt / nbMensualites;
  
  const assuranceMensuelle = (inputs.montant_emprunt * inputs.taux_assurance / 100) / 12;
  const mensualiteTotale = mensualiteCredit + assuranceMensuelle;
  
  // TAEG approximatif
  const taeg = inputs.taux_interet + inputs.taux_assurance;
  
  // Charges totales mensuelles
  const chargesCopro = inputs.charges_copro_mois;
  const taxeFonciere = inputs.taxe_fonciere_mois;
  const assurancePNO = inputs.assurance_pno_mois;
  const fluides = inputs.eau_mois + inputs.electricite_mois + inputs.gaz_mois + inputs.internet_mois + inputs.entretien_chaudiere_mois;
  const gestionLocative = inputs.loyer_mensuel * inputs.gestion_locative_pct / 100;
  const gli = inputs.loyer_mensuel * inputs.gli_pct / 100;
  const totalChargesMensuelles = chargesCopro + taxeFonciere + assurancePNO + fluides + gestionLocative + gli;
  const totalChargesAnnuelles = totalChargesMensuelles * 12;
  
  // Loyers annuels
  const loyersAnnuels = inputs.loyer_mensuel * 12;
  
  // Intérêts première année (approximation)
  const interetsAnnuels = inputs.montant_emprunt * inputs.taux_interet / 100;
  const assuranceAnnuelle = inputs.montant_emprunt * inputs.taux_assurance / 100;
  
  // Amortissements (pour LMNP)
  const amortissementBien = inputs.prix_achat * 0.025; // 2.5% du bien sur 40 ans
  const amortissementTravaux = inputs.travaux / 10; // Travaux sur 10 ans
  const amortissementMobilier = inputs.mobilier / 7; // Mobilier sur 7 ans
  const totalAmortissements = amortissementBien + amortissementTravaux + amortissementMobilier;

  const calculateRentability = async () => {
    setLoading(true);
    try {
      // Calcul local des résultats
      const rendementBrut = (loyersAnnuels / totalInvestissement) * 100;
      
      // Calcul des impôts selon le régime
      let impots = 0;
      let revenuImposable = 0;
      
      if (inputs.regime_fiscal === 'micro_bic') {
        // Micro-BIC : abattement 50%
        revenuImposable = loyersAnnuels * 0.5;
        impots = revenuImposable * (inputs.tmi / 100 + 0.172); // TMI + prélèvements sociaux
      } else if (inputs.regime_fiscal === 'lmnp') {
        // LMNP Réel : charges + amortissements déductibles
        revenuImposable = Math.max(0, loyersAnnuels - totalChargesAnnuelles - interetsAnnuels - assuranceAnnuelle - totalAmortissements);
        impots = revenuImposable * (inputs.tmi / 100 + 0.172);
      } else if (inputs.regime_fiscal === 'sci_is') {
        // SCI IS : 15% jusqu'à 42500€, 25% au-delà
        const benefice = loyersAnnuels - totalChargesAnnuelles - interetsAnnuels - totalAmortissements;
        if (benefice > 0) {
          if (benefice <= 42500) {
            impots = benefice * 0.15;
          } else {
            impots = 42500 * 0.15 + (benefice - 42500) * 0.25;
          }
        }
        revenuImposable = benefice;
      } else {
        // Autres régimes
        revenuImposable = loyersAnnuels - totalChargesAnnuelles;
        impots = revenuImposable * (inputs.tmi / 100 + 0.172);
      }
      
      const impotsMensuels = impots / 12;
      
      // Cash-flow
      const cashFlowMensuel = inputs.loyer_mensuel - totalChargesMensuelles - mensualiteTotale - impotsMensuels;
      const cashFlowAnnuel = cashFlowMensuel * 12;
      
      // Rendement net
      const rendementNet = (cashFlowAnnuel / apportPersonnel) * 100;
      
      setResults({
        rendement_brut: rendementBrut,
        rendement_net: rendementNet,
        cash_flow_mensuel: cashFlowMensuel,
        cash_flow_annuel: cashFlowAnnuel,
        cout_total: totalInvestissement,
        montant_emprunt: inputs.montant_emprunt,
        mensualite_credit: mensualiteCredit,
        mensualite_assurance: assuranceMensuelle,
        mensualite_totale: mensualiteTotale,
        apport: apportPersonnel,
        taeg: taeg,
        loyers_annuels: loyersAnnuels,
        charges_annuelles: totalChargesAnnuelles,
        interets_annuels: interetsAnnuels,
        assurance_emprunt_annuelle: assuranceAnnuelle,
        amortissements: totalAmortissements,
        revenu_imposable: revenuImposable,
        impots_annuels: impots,
        regime_fiscal: inputs.regime_fiscal,
        type_location: inputs.type_location
      });
      
      toast.success('Calcul effectué !');
    } catch (error) {
      toast.error('Erreur lors du calcul');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  // Auto-calculate removed - user must click "Calculer" button

  // Load saved calculations when tab changes
  useEffect(() => {
    if (activeTab === 'guide') {
      axios.get(`${API}/calculator/history`, { withCredentials: true })
        .then(resp => setSavedCalcs(resp.data))
        .catch(() => {});
    }
  }, [activeTab]);

  const resetCalculator = () => {
    setResults(null);
    setInputs({
      project_name: '',
      type_location: 'meuble',
      prix_achat: 200000,
      frais_notaire: 15000,
      travaux: 20000,
      mobilier: 0,
      frais_courtage: 0,
      frais_garantie: 0,
      frais_dossier: 0,
      montant_emprunt: 180000,
      taux_interet: 3.5,
      taux_assurance: 0.36,
      duree_pret: 20,
      loyer_mensuel: 900,
      charges_copro_mois: 100,
      taxe_fonciere_mois: 100,
      assurance_pno_mois: 30,
      eau_mois: 0,
      electricite_mois: 0,
      gaz_mois: 0,
      internet_mois: 0,
      entretien_chaudiere_mois: 0,
      gestion_locative_pct: 0,
      gli_pct: 0,
      regime_fiscal: 'lmnp',
      tmi: 30
    });
    toast.success('Nouveau calcul pret !');
  };

  const downloadPDF = async () => {
    if (user?.subscription_tier === 'free') {
      toast.error('Le dossier PDF est réservé aux abonnés Pro');
      return;
    }

    setPdfLoading(true);
    try {
      const response = await fetch(`${API}/calculator/pdf`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ inputs, results, project_name: inputs.project_name })
      });

      if (!response.ok) throw new Error('Erreur serveur');

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `dossier_bancaire_10M_${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success('PDF téléchargé !');
    } catch (error) {
      toast.error('Erreur lors de la génération du PDF');
    } finally {
      setPdfLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(value);
  };

  const formatPercent = (value) => {
    return `${value?.toFixed(2) || '0.00'}%`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-50 shadow-sm">
        <div className="max-w-6xl mx-auto px-3 sm:px-4 py-2 sm:py-3">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 sm:gap-4 min-w-0">
              <Link to="/dashboard">
                <Button variant="ghost" size="sm" className="text-slate-600 hover:text-[#0D47A1] hover:bg-[#0D47A1]/5 flex-shrink-0 px-2 sm:px-3">
                  <ChevronLeft className="h-4 w-4" />
                  <span className="hidden sm:inline ml-1">Dashboard</span>
                </Button>
              </Link>
              <Link to="/dashboard" className="hidden md:flex">
                <img src="/logo-horizontal.png" alt="10 Millions Investor" className="h-16 sm:h-20 w-auto" data-testid="header-logo" />
              </Link>
            </div>
            
            <div className="flex items-center gap-2 flex-shrink-0">
              <Button
                onClick={resetCalculator}
                variant="outline"
                size="sm"
                className="text-xs sm:text-sm"
                data-testid="reset-calc-btn"
              >
                <Trash2 className="h-3.5 w-3.5 sm:mr-1" />
                <span className="hidden sm:inline">Nouveau</span>
              </Button>
              {results && (
                <Button
                  onClick={downloadPDF}
                  disabled={pdfLoading}
                  size="sm"
                  className="bg-[#0D47A1] hover:bg-[#0A367A] text-xs sm:text-sm"
                  data-testid="download-pdf-btn"
                >
                  {pdfLoading ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin sm:mr-1" />
                  ) : (
                    <Download className="h-3.5 w-3.5 sm:mr-1" />
                  )}
                  <span className="hidden sm:inline">PDF</span>
                </Button>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Hero */}
      <div className="bg-gradient-to-r from-[#0D47A1] to-[#1565C0] text-white py-5 sm:py-8">
        <div className="max-w-6xl mx-auto px-3 sm:px-4 text-center">
          <h1 className="text-lg sm:text-2xl md:text-3xl font-bold mb-1 sm:mb-2">Calculateur de Rentabilite</h1>
          <p className="text-blue-100 text-xs sm:text-base">Analysez la rentabilite de votre investissement immobilier</p>
        </div>
      </div>

      {/* Tabs Navigation */}
      <div className="bg-white border-b shadow-sm overflow-x-auto">
        <div className="max-w-6xl mx-auto px-3 sm:px-4">
          <div className="flex gap-0 min-w-0">
            <button
              onClick={() => setActiveTab('calculator')}
              className={`px-3 sm:px-4 py-2.5 sm:py-3 flex items-center gap-1.5 sm:gap-2 border-b-2 transition-colors whitespace-nowrap text-xs sm:text-sm ${
                activeTab === 'calculator' 
                  ? 'border-[#0D47A1] text-[#0D47A1] font-medium' 
                  : 'border-transparent text-slate-500 hover:text-slate-700'
              }`}
            >
              <Calculator className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
              Calculateur
            </button>
            <button
              onClick={() => setActiveTab('comparison')}
              className={`px-3 sm:px-4 py-2.5 sm:py-3 flex items-center gap-1.5 sm:gap-2 border-b-2 transition-colors whitespace-nowrap text-xs sm:text-sm ${
                activeTab === 'comparison' 
                  ? 'border-[#0D47A1] text-[#0D47A1] font-medium' 
                  : 'border-transparent text-slate-500 hover:text-slate-700'
              }`}
            >
              <Scale className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
              Comparaison
            </button>
            <button
              onClick={() => setActiveTab('guide')}
              className={`px-3 sm:px-4 py-2.5 sm:py-3 flex items-center gap-1.5 sm:gap-2 border-b-2 transition-colors whitespace-nowrap text-xs sm:text-sm ${
                activeTab === 'guide' 
                  ? 'border-[#0D47A1] text-[#0D47A1] font-medium' 
                  : 'border-transparent text-slate-500 hover:text-slate-700'
              }`}
            >
              <Save className="h-3.5 w-3.5 sm:h-4 sm:w-4" />
              Mes Calculs
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <main className="max-w-6xl mx-auto px-3 sm:px-4 py-4 sm:py-6 overflow-x-hidden">
        {activeTab === 'calculator' && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 sm:gap-6">
            {/* Left Column - Inputs */}
            <div className="lg:col-span-2 space-y-4 sm:space-y-6">
              
              {/* Nom du projet */}
              <Card className="border-0 shadow-md">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <FileText className="h-5 w-5 text-[#0D47A1]" />
                    Nom du projet
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <Input
                    type="text"
                    placeholder="Ex: Appartement T3 Lyon 7e, Studio Villeurbanne..."
                    value={inputs.project_name}
                    onChange={(e) => handleInputChange('project_name', e.target.value)}
                    className="text-base"
                    data-testid="input-project-name"
                  />
                  <p className="text-xs text-slate-400 mt-1">Ce nom apparaitra dans le PDF pour identifier facilement votre projet</p>
                </CardContent>
              </Card>

              {/* Type de location */}
              <Card className="border-0 shadow-md">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Home className="h-5 w-5 text-[#0D47A1]" />
                    Type de location
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant={inputs.type_location === 'meuble' ? 'default' : 'outline'}
                      className={inputs.type_location === 'meuble' ? 'bg-[#0D47A1]' : ''}
                      onClick={() => handleInputChange('type_location', 'meuble')}
                    >
                      <Sofa className="h-4 w-4 mr-2" />
                      Meublé
                    </Button>
                    <Button
                      type="button"
                      variant={inputs.type_location === 'nu' ? 'default' : 'outline'}
                      className={inputs.type_location === 'nu' ? 'bg-[#0D47A1]' : ''}
                      onClick={() => handleInputChange('type_location', 'nu')}
                    >
                      <Home className="h-4 w-4 mr-2" />
                      Nu
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* 1. Investissement */}
              <Card className="border-0 shadow-md">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <span className="bg-[#0D47A1] text-white rounded-full w-6 h-6 flex items-center justify-center text-sm">1</span>
                    Investissement
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-slate-600">Prix d'achat</Label>
                      <div className="relative mt-1">
                        <Input
                          type="number"
                          value={displayValue(inputs.prix_achat)}
                          onChange={(e) => handleInputChange('prix_achat', parseFloat(e.target.value) || 0)}
                          onFocus={handleFocus}
                          placeholder="0"
                          className="pr-8"
                          data-testid="input-prix-achat"
                        />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">€</span>
                      </div>
                    </div>
                    <div>
                      <Label className="text-slate-600">Frais de notaire</Label>
                      <div className="relative mt-1">
                        <Input
                          type="number"
                          value={displayValue(inputs.frais_notaire)}
                          onChange={(e) => handleInputChange('frais_notaire', parseFloat(e.target.value) || 0)}
                          onFocus={handleFocus}
                          placeholder="0"
                          className="pr-8"
                        />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">€</span>
                      </div>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label className="text-slate-600">Travaux</Label>
                      <div className="relative mt-1">
                        <Input
                          type="number"
                          value={displayValue(inputs.travaux)}
                          onChange={(e) => handleInputChange('travaux', parseFloat(e.target.value) || 0)}
                          onFocus={handleFocus}
                          placeholder="0"
                          className="pr-8"
                        />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">€</span>
                      </div>
                    </div>
                    <div>
                      <Label className="text-slate-600">Mobilier (optionnel)</Label>
                      <div className="relative mt-1">
                        <Input
                          type="number"
                          value={displayValue(inputs.mobilier)}
                          onChange={(e) => handleInputChange('mobilier', parseFloat(e.target.value) || 0)}
                          onFocus={handleFocus}
                          placeholder="0"
                          className="pr-8"
                        />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">€</span>
                      </div>
                      <p className="text-xs text-slate-400 mt-1">Amortissable sur 7 ans</p>
                    </div>
                  </div>
                  
                  {/* Total investissement */}
                  <div className="bg-[#0D47A1]/5 rounded-lg p-3 flex justify-between items-center">
                    <span className="font-medium text-slate-700">Total investissement</span>
                    <span className="text-xl font-bold text-[#0D47A1]">{formatCurrency(totalInvestissement)}</span>
                  </div>
                </CardContent>
              </Card>

              {/* Frais de financement (optionnel) */}
              <Card className="border-0 shadow-md">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <CreditCard className="h-5 w-5 text-slate-400" />
                    Frais de financement (optionnel)
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    <div>
                      <Label className="text-slate-600 text-xs sm:text-sm">Frais de courtage</Label>
                      <div className="relative mt-1">
                        <Input
                          type="number"
                          value={displayValue(inputs.frais_courtage)}
                          onChange={(e) => handleInputChange('frais_courtage', parseFloat(e.target.value) || 0)}
                          onFocus={handleFocus}
                          placeholder="0"
                          className="pr-8"
                        />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">€</span>
                      </div>
                    </div>
                    <div>
                      <Label className="text-slate-600 text-sm">Frais de garantie</Label>
                      <div className="relative mt-1">
                        <Input
                          type="number"
                          value={displayValue(inputs.frais_garantie)}
                          onChange={(e) => handleInputChange('frais_garantie', parseFloat(e.target.value) || 0)}
                          onFocus={handleFocus}
                          placeholder="0"
                          className="pr-8"
                        />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">€</span>
                      </div>
                    </div>
                    <div>
                      <Label className="text-slate-600 text-sm">Frais de dossier</Label>
                      <div className="relative mt-1">
                        <Input
                          type="number"
                          value={displayValue(inputs.frais_dossier)}
                          onChange={(e) => handleInputChange('frais_dossier', parseFloat(e.target.value) || 0)}
                          onFocus={handleFocus}
                          placeholder="0"
                          className="pr-8"
                        />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">€</span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* 2. Financement */}
              <Card className="border-0 shadow-md">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <span className="bg-[#0D47A1] text-white rounded-full w-6 h-6 flex items-center justify-center text-sm">2</span>
                    Financement
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label className="text-slate-600">Montant de l'emprunt</Label>
                    <div className="relative mt-1">
                      <Input
                        type="number"
                        value={displayValue(inputs.montant_emprunt)}
                        onChange={(e) => handleInputChange('montant_emprunt', parseFloat(e.target.value) || 0)}
                        onFocus={handleFocus}
                        placeholder="0"
                        className="pr-8"
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">€</span>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    <div>
                      <Label className="text-slate-600 text-xs sm:text-sm">Taux d'intérêt nominal (%)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={displayValue(inputs.taux_interet)}
                        onChange={(e) => handleInputChange('taux_interet', parseFloat(e.target.value) || 0)}
                        onFocus={handleFocus}
                        placeholder="0"
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-slate-600 text-sm">Taux assurance (%)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={displayValue(inputs.taux_assurance)}
                        onChange={(e) => handleInputChange('taux_assurance', parseFloat(e.target.value) || 0)}
                        onFocus={handleFocus}
                        placeholder="0"
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label className="text-slate-600 text-sm">Durée (années)</Label>
                      <Input
                        type="number"
                        value={displayValue(inputs.duree_pret)}
                        onChange={(e) => handleInputChange('duree_pret', parseInt(e.target.value) || 20)}
                        onFocus={handleFocus}
                        placeholder="20"
                        className="mt-1"
                      />
                    </div>
                  </div>
                  
                  {/* Résumé financement */}
                  <div className="bg-slate-50 rounded-lg p-4 space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-600">Mensualité crédit</span>
                      <span className="font-semibold">{formatCurrency(mensualiteCredit)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-600">Assurance mensuelle</span>
                      <span className="font-semibold">{formatCurrency(assuranceMensuelle)}</span>
                    </div>
                    <div className="flex justify-between text-sm font-medium border-t pt-2">
                      <span className="text-slate-700">Mensualité totale</span>
                      <span className="text-[#0D47A1] font-bold">{formatCurrency(mensualiteTotale)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-600">TAEG</span>
                      <span className="font-semibold">{formatPercent(taeg)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-600">Apport personnel</span>
                      <span className="font-semibold">{formatCurrency(apportPersonnel)}</span>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* 3. Revenus */}
              <Card className="border-0 shadow-md">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <span className="bg-[#00B050] text-white rounded-full w-6 h-6 flex items-center justify-center text-sm">3</span>
                    Revenus
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div>
                    <Label className="text-slate-600">Loyer mensuel hors charges (HC)</Label>
                    <div className="relative mt-1">
                      <Input
                        type="number"
                        value={displayValue(inputs.loyer_mensuel)}
                        onChange={(e) => handleInputChange('loyer_mensuel', parseFloat(e.target.value) || 0)}
                        onFocus={handleFocus}
                        placeholder="0"
                        className="pr-16"
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">€ / mois</span>
                    </div>
                    <p className="text-sm text-slate-500 mt-1">soit {formatCurrency(loyersAnnuels)} / an</p>
                  </div>
                </CardContent>
              </Card>

              {/* 4. Charges mensuelles */}
              <Card className="border-0 shadow-md">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <span className="bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm">4</span>
                    Charges mensuelles
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    <div>
                      <Label className="text-slate-600 text-xs sm:text-sm">Charges copropriété / mois</Label>
                      <div className="relative mt-1">
                        <Input
                          type="number"
                          value={displayValue(inputs.charges_copro_mois)}
                          onChange={(e) => handleInputChange('charges_copro_mois', parseFloat(e.target.value) || 0)}
                          onFocus={handleFocus}
                          placeholder="0"
                          className="pr-8"
                        />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">€</span>
                      </div>
                      <p className="text-xs text-slate-400 mt-1">{formatCurrency(inputs.charges_copro_mois * 12)} / an</p>
                    </div>
                    <div>
                      <Label className="text-slate-600 text-sm">Taxe foncière / mois</Label>
                      <div className="relative mt-1">
                        <Input
                          type="number"
                          value={displayValue(inputs.taxe_fonciere_mois)}
                          onChange={(e) => handleInputChange('taxe_fonciere_mois', parseFloat(e.target.value) || 0)}
                          onFocus={handleFocus}
                          placeholder="0"
                          className="pr-8"
                        />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">€</span>
                      </div>
                      <p className="text-xs text-slate-400 mt-1">{formatCurrency(inputs.taxe_fonciere_mois * 12)} / an</p>
                    </div>
                    <div>
                      <Label className="text-slate-600 text-sm">Assurance PNO / mois</Label>
                      <div className="relative mt-1">
                        <Input
                          type="number"
                          value={displayValue(inputs.assurance_pno_mois)}
                          onChange={(e) => handleInputChange('assurance_pno_mois', parseFloat(e.target.value) || 0)}
                          onFocus={handleFocus}
                          placeholder="0"
                          className="pr-8"
                        />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">€</span>
                      </div>
                      <p className="text-xs text-slate-400 mt-1">{formatCurrency(inputs.assurance_pno_mois * 12)} / an</p>
                    </div>
                  </div>

                  {/* Services et fluides */}
                  <div className="border-t pt-4">
                    <Label className="text-slate-500 text-xs sm:text-sm mb-3 block">Services et fluides / mois (optionnel)</Label>
                    <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
                      <div>
                        <Label className="text-xs text-slate-400 flex items-center gap-1">
                          <Droplets className="h-3 w-3" /> Eau
                        </Label>
                        <Input
                          type="number"
                          value={displayValue(inputs.eau_mois)}
                          onChange={(e) => handleInputChange('eau_mois', parseFloat(e.target.value) || 0)}
                          onFocus={handleFocus}
                          placeholder="0"
                          className="mt-1 text-sm"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-slate-400 flex items-center gap-1">
                          <Zap className="h-3 w-3" /> Élec.
                        </Label>
                        <Input
                          type="number"
                          value={displayValue(inputs.electricite_mois)}
                          onChange={(e) => handleInputChange('electricite_mois', parseFloat(e.target.value) || 0)}
                          onFocus={handleFocus}
                          placeholder="0"
                          className="mt-1 text-sm"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-slate-400 flex items-center gap-1">
                          <Flame className="h-3 w-3" /> Gaz
                        </Label>
                        <Input
                          type="number"
                          value={displayValue(inputs.gaz_mois)}
                          onChange={(e) => handleInputChange('gaz_mois', parseFloat(e.target.value) || 0)}
                          onFocus={handleFocus}
                          placeholder="0"
                          className="mt-1 text-sm"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-slate-400 flex items-center gap-1">
                          <Wifi className="h-3 w-3" /> Internet
                        </Label>
                        <Input
                          type="number"
                          value={displayValue(inputs.internet_mois)}
                          onChange={(e) => handleInputChange('internet_mois', parseFloat(e.target.value) || 0)}
                          onFocus={handleFocus}
                          placeholder="0"
                          className="mt-1 text-sm"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-slate-400 flex items-center gap-1">
                          <Wrench className="h-3 w-3" /> Chaud.
                        </Label>
                        <Input
                          type="number"
                          value={displayValue(inputs.entretien_chaudiere_mois)}
                          onChange={(e) => handleInputChange('entretien_chaudiere_mois', parseFloat(e.target.value) || 0)}
                          onFocus={handleFocus}
                          placeholder="0"
                          className="mt-1 text-sm"
                        />
                      </div>
                    </div>
                  </div>

                  {/* Gestion et garanties */}
                  <div className="border-t pt-4">
                    <Label className="text-slate-500 text-sm mb-3 block flex items-center gap-2">
                      <Building2 className="h-4 w-4" /> Gestion et garanties (optionnel)
                    </Label>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label className="text-slate-600 text-sm">Frais de gestion locative (% du loyer)</Label>
                        <Select
                          value={inputs.gestion_locative_pct.toString()}
                          onValueChange={(value) => handleInputChange('gestion_locative_pct', parseFloat(value))}
                        >
                          <SelectTrigger className="mt-1">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="0">Gestion en direct</SelectItem>
                            <SelectItem value="5">5%</SelectItem>
                            <SelectItem value="7">7%</SelectItem>
                            <SelectItem value="8">8%</SelectItem>
                            <SelectItem value="10">10%</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-slate-600 text-sm">Garantie Loyers Impayés - GLI (% du loyer)</Label>
                        <Select
                          value={inputs.gli_pct.toString()}
                          onValueChange={(value) => handleInputChange('gli_pct', parseFloat(value))}
                        >
                          <SelectTrigger className="mt-1">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="0">Pas de GLI</SelectItem>
                            <SelectItem value="2.5">2.5%</SelectItem>
                            <SelectItem value="3">3%</SelectItem>
                            <SelectItem value="3.5">3.5%</SelectItem>
                            <SelectItem value="4">4%</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>

                  {/* Total charges */}
                  <div className="bg-red-50 rounded-lg p-3 flex justify-between items-center">
                    <span className="font-medium text-slate-700">Total charges mensuelles</span>
                    <div className="text-right">
                      <span className="text-xl font-bold text-red-600">{formatCurrency(totalChargesMensuelles)}</span>
                      <p className="text-xs text-slate-500">{formatCurrency(totalChargesAnnuelles)} / an</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* 5. Fiscalité */}
              <Card className="border-0 shadow-md">
                <CardHeader className="pb-3">
                  <CardTitle className="text-base flex items-center gap-2">
                    <span className="bg-purple-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm">5</span>
                    Fiscalité
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <Label className="text-slate-600">Régime fiscal</Label>
                    <Select
                      value={inputs.regime_fiscal}
                      onValueChange={(value) => handleInputChange('regime_fiscal', value)}
                    >
                      <SelectTrigger className="mt-1" data-testid="select-regime">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="micro_bic">Micro-BIC (abattement 50%)</SelectItem>
                        <SelectItem value="reel_bic">Réel BIC</SelectItem>
                        <SelectItem value="lmnp">LMNP</SelectItem>
                        <SelectItem value="lmp">LMP</SelectItem>
                        <SelectItem value="sci_is">SCI à l'IS</SelectItem>
                        <SelectItem value="sci_ir">SCI à l'IR</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-slate-600">Tranche marginale d'imposition (%)</Label>
                    <div className="flex gap-2 mt-2">
                      {[0, 11, 30, 41, 45].map((tmi) => (
                        <Button
                          key={tmi}
                          type="button"
                          variant={inputs.tmi === tmi ? 'default' : 'outline'}
                          className={`flex-1 ${inputs.tmi === tmi ? 'bg-[#0D47A1]' : ''}`}
                          onClick={() => handleInputChange('tmi', tmi)}
                        >
                          {tmi}%
                        </Button>
                      ))}
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

              {/* Bouton Calculer */}
              <div className="sticky bottom-4 z-40 lg:hidden">
                <Button
                  onClick={calculateRentability}
                  disabled={loading}
                  className="w-full bg-gradient-to-r from-[#00B050] to-[#0D47A1] text-white shadow-lg py-6 text-base font-bold"
                  data-testid="calculate-btn"
                >
                  {loading ? <Loader2 className="h-5 w-5 mr-2 animate-spin" /> : <Calculator className="h-5 w-5 mr-2" />}
                  Calculer la rentabilite
                </Button>
              </div>

            {/* Right Column - Results */}
            <div className="lg:col-span-1">
              <div className="sticky top-32 space-y-4">
                {/* Bouton Calculer Desktop */}
                <Button
                  onClick={calculateRentability}
                  disabled={loading}
                  className="w-full bg-gradient-to-r from-[#00B050] to-[#0D47A1] text-white py-5 text-base font-bold hidden lg:flex"
                  data-testid="calculate-btn-desktop"
                >
                  {loading ? <Loader2 className="h-5 w-5 mr-2 animate-spin" /> : <Calculator className="h-5 w-5 mr-2" />}
                  Calculer la rentabilite
                </Button>
                {/* Rentabilités */}
                <Card className="border-0 shadow-md">
                  <CardHeader className="pb-2 bg-slate-50">
                    <CardTitle className="text-base flex items-center gap-2">
                      <BarChart3 className="h-5 w-5 text-[#0D47A1]" />
                      Rentabilités
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-4 space-y-4">
                    <div className="text-center p-4 bg-blue-50 rounded-lg">
                      <p className="text-xs uppercase tracking-wider text-slate-500 mb-1">Rentabilité brute</p>
                      <p className="text-3xl font-bold text-[#0D47A1]">{formatPercent(results?.rendement_brut)}</p>
                      <p className="text-xs text-slate-400 mt-1">Loyers annuels / Investissement total</p>
                    </div>
                    <div className="text-center p-4 bg-slate-50 rounded-lg">
                      <p className="text-xs uppercase tracking-wider text-slate-500 mb-1">Rentabilité nette</p>
                      <p className={`text-3xl font-bold ${(results?.rendement_net || 0) >= 0 ? 'text-[#00B050]' : 'text-red-500'}`}>
                        {formatPercent(results?.rendement_net)}
                      </p>
                      <p className="text-xs text-slate-400 mt-1">Cash-flow annuel / Apport personnel</p>
                    </div>
                  </CardContent>
                </Card>

                {/* Cash-flow */}
                <Card className="border-0 shadow-md">
                  <CardHeader className="pb-2 bg-slate-50">
                    <CardTitle className="text-base flex items-center gap-2">
                      <PiggyBank className="h-5 w-5 text-[#00B050]" />
                      Cash-flow
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-4 space-y-4">
                    <div className={`text-center p-4 rounded-lg ${(results?.cash_flow_mensuel || 0) >= 0 ? 'bg-green-50' : 'bg-red-50'}`}>
                      <p className="text-xs uppercase tracking-wider text-slate-500 mb-1">Mensuel</p>
                      <p className={`text-3xl font-bold ${(results?.cash_flow_mensuel || 0) >= 0 ? 'text-[#00B050]' : 'text-red-500'}`}>
                        {formatCurrency(results?.cash_flow_mensuel || 0)}
                      </p>
                      <p className="text-xs text-slate-400 mt-1">Loyer - Charges - Mensualité - Impôts</p>
                    </div>
                    <div className="text-center p-4 bg-slate-50 rounded-lg">
                      <p className="text-xs uppercase tracking-wider text-slate-500 mb-1">Annuel</p>
                      <p className={`text-2xl font-bold ${(results?.cash_flow_annuel || 0) >= 0 ? 'text-[#00B050]' : 'text-red-500'}`}>
                        {formatCurrency(results?.cash_flow_annuel || 0)}
                      </p>
                      <p className="text-xs text-slate-400 mt-1">Cash-flow mensuel × 12</p>
                    </div>
                  </CardContent>
                </Card>

                {/* Détails Fiscaux */}
                <Card className="border-0 shadow-md">
                  <CardHeader className="pb-2 bg-slate-50">
                    <CardTitle className="text-base flex items-center gap-2">
                      <Receipt className="h-5 w-5 text-purple-600" />
                      Détails Fiscaux
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-4">
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-slate-500">Régime fiscal</span>
                        <span className="font-semibold">{inputs.regime_fiscal.toUpperCase()}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-500">Type</span>
                        <span className="font-semibold">{inputs.type_location === 'meuble' ? '🛋️ Meublé' : '🏠 Nu'}</span>
                      </div>
                      <div className="border-t my-2"></div>
                      <div className="flex justify-between">
                        <span className="text-slate-500">Loyers annuels</span>
                        <span className="font-semibold">{formatCurrency(loyersAnnuels)}</span>
                      </div>
                      <div className="flex justify-between text-red-600">
                        <span>Charges annuelles</span>
                        <span>- {formatCurrency(totalChargesAnnuelles)}</span>
                      </div>
                      <div className="flex justify-between text-red-600">
                        <span>Intérêts emprunt</span>
                        <span>- {formatCurrency(results?.interets_annuels || interetsAnnuels)}</span>
                      </div>
                      <div className="flex justify-between text-red-600">
                        <span>Assurance emprunt</span>
                        <span>- {formatCurrency(results?.assurance_emprunt_annuelle || assuranceAnnuelle)}</span>
                      </div>
                      {inputs.regime_fiscal === 'lmnp' && (
                        <div className="flex justify-between text-red-600">
                          <span>Amortissements</span>
                          <span>- {formatCurrency(totalAmortissements)}</span>
                        </div>
                      )}
                      <div className="border-t my-2"></div>
                      <div className="flex justify-between">
                        <span className="text-slate-600 font-medium">Revenu imposable</span>
                        <span className="font-bold">{formatCurrency(results?.revenu_imposable || 0)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-600 font-medium">Impôts annuels</span>
                        <span className="font-bold text-red-600">{formatCurrency(results?.impots_annuels || 0)}</span>
                      </div>
                    </div>
                    
                    {inputs.regime_fiscal === 'lmnp' && (results?.revenu_imposable || 0) <= 0 && (
                      <div className="mt-4 p-3 bg-green-50 rounded-lg border border-green-200">
                        <div className="flex items-start gap-2">
                          <CheckCircle2 className="h-5 w-5 text-green-600 flex-shrink-0" />
                          <p className="text-sm text-green-700">
                            <strong>Avantage LMNP :</strong> Grâce aux amortissements, vous ne payez pas d'impôts sur vos revenus locatifs !
                          </p>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Save + Download PDF Buttons */}
                <div className="flex gap-2">
                  <Button
                    onClick={async () => {
                      try {
                        await axios.post(`${API}/calculator/save`, {
                          inputs,
                          results,
                          name: inputs.project_name || `Calcul du ${new Date().toLocaleDateString('fr-FR')}`
                        }, { withCredentials: true });
                        toast.success('Calcul sauvegard\u00e9 !');
                      } catch {
                        toast.error('Erreur lors de la sauvegarde');
                      }
                    }}
                    variant="outline"
                    className="flex-1 h-12 border-[#0D47A1] text-[#0D47A1] hover:bg-[#0D47A1]/5"
                    data-testid="save-calc-inline-btn"
                  >
                    <Save className="h-4 w-4 mr-2" />
                    Sauvegarder
                  </Button>
                  <Button
                    onClick={downloadPDF}
                    disabled={pdfLoading}
                    className="flex-1 bg-[#0D47A1] hover:bg-[#0A367A] h-12"
                  >
                    {pdfLoading ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <FileText className="h-4 w-4 mr-2" />
                    )}
                    T&eacute;l&eacute;charger PDF
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'comparison' && (
          <div className="space-y-6">
            <Card className="border-0 shadow-md">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Scale className="h-5 w-5 text-[#0D47A1]" />
                  Comparaison des régimes fiscaux
                </CardTitle>
                <p className="text-slate-500 text-sm mt-1">
                  Simulez le même investissement selon chaque régime pour trouver le plus avantageux
                </p>
              </CardHeader>
              <CardContent>
                {/* Tableau de comparaison */}
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse" data-testid="fiscal-comparison-table">
                    <thead>
                      <tr className="border-b-2 border-slate-200">
                        <th className="text-left py-4 px-3 font-semibold text-slate-700">Régime</th>
                        <th className="text-center py-4 px-3 font-semibold text-slate-700">Type</th>
                        <th className="text-right py-4 px-3 font-semibold text-slate-700">Revenu imposable</th>
                        <th className="text-right py-4 px-3 font-semibold text-slate-700">Impôts annuels</th>
                        <th className="text-right py-4 px-3 font-semibold text-slate-700">Cash-flow net</th>
                        <th className="text-right py-4 px-3 font-semibold text-slate-700">Rendement net</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(() => {
                        // Calculer pour chaque régime
                        const regimes = [
                          { id: 'micro_bic', name: 'Micro-BIC', type: 'Meublé', abattement: 0.5 },
                          { id: 'reel_bic', name: 'Réel BIC', type: 'Meublé', abattement: 0 },
                          { id: 'lmnp', name: 'LMNP', type: 'Meublé', abattement: 0 },
                          { id: 'lmp', name: 'LMP', type: 'Meublé', abattement: 0 },
                          { id: 'sci_is', name: 'SCI à l\'IS', type: 'Nu/Meublé', abattement: 0 },
                          { id: 'sci_ir', name: 'SCI à l\'IR', type: 'Nu', abattement: 0 },
                          { id: 'micro_foncier', name: 'Micro-Foncier', type: 'Nu', abattement: 0.3 },
                          { id: 'reel_foncier', name: 'Réel Foncier', type: 'Nu', abattement: 0 },
                        ];

                        const calculateForRegime = (regime) => {
                          let revenuImposable = 0;
                          let impots = 0;

                          if (regime.id === 'micro_bic') {
                            revenuImposable = loyersAnnuels * 0.5;
                            impots = revenuImposable * (inputs.tmi / 100 + 0.172);
                          } else if (regime.id === 'micro_foncier') {
                            revenuImposable = loyersAnnuels * 0.7;
                            impots = revenuImposable * (inputs.tmi / 100 + 0.172);
                          } else if (regime.id === 'lmnp') {
                            revenuImposable = Math.max(0, loyersAnnuels - totalChargesAnnuelles - interetsAnnuels - assuranceAnnuelle - totalAmortissements);
                            impots = revenuImposable * (inputs.tmi / 100 + 0.172);
                          } else if (regime.id === 'lmp') {
                            revenuImposable = Math.max(0, loyersAnnuels - totalChargesAnnuelles - interetsAnnuels - assuranceAnnuelle - totalAmortissements);
                            impots = revenuImposable * (inputs.tmi / 100 + 0.172);
                          } else if (regime.id === 'sci_is') {
                            const benefice = loyersAnnuels - totalChargesAnnuelles - interetsAnnuels - totalAmortissements;
                            if (benefice > 0) {
                              impots = benefice <= 42500 ? benefice * 0.15 : 42500 * 0.15 + (benefice - 42500) * 0.25;
                            }
                            revenuImposable = benefice;
                          } else if (regime.id === 'sci_ir' || regime.id === 'reel_foncier') {
                            revenuImposable = loyersAnnuels - totalChargesAnnuelles - interetsAnnuels;
                            impots = Math.max(0, revenuImposable) * (inputs.tmi / 100 + 0.172);
                          } else if (regime.id === 'reel_bic') {
                            revenuImposable = loyersAnnuels - totalChargesAnnuelles - interetsAnnuels - assuranceAnnuelle;
                            impots = Math.max(0, revenuImposable) * (inputs.tmi / 100 + 0.172);
                          }

                          const cashFlowNet = loyersAnnuels - totalChargesAnnuelles - (mensualiteTotale * 12) - impots;
                          const rendementNet = (cashFlowNet / apportPersonnel) * 100;

                          return { revenuImposable, impots, cashFlowNet, rendementNet };
                        };

                        // Trouver le meilleur régime
                        let bestRegime = null;
                        let bestCashFlow = -Infinity;
                        const results = regimes.map(r => {
                          const calc = calculateForRegime(r);
                          if (calc.cashFlowNet > bestCashFlow) {
                            bestCashFlow = calc.cashFlowNet;
                            bestRegime = r.id;
                          }
                          return { ...r, ...calc };
                        });

                        return results.map((regime, idx) => (
                          <tr 
                            key={regime.id} 
                            className={`border-b border-slate-100 hover:bg-slate-50 transition-colors ${
                              regime.id === bestRegime ? 'bg-green-50 hover:bg-green-100' : ''
                            } ${regime.id === inputs.regime_fiscal ? 'ring-2 ring-[#0D47A1] ring-inset' : ''}`}
                          >
                            <td className="py-4 px-3">
                              <div className="flex items-center gap-2">
                                {regime.id === bestRegime && (
                                  <span className="bg-[#00B050] text-white text-xs px-2 py-0.5 rounded-full">Optimal</span>
                                )}
                                <span className="font-medium text-slate-800">{regime.name}</span>
                              </div>
                            </td>
                            <td className="text-center py-4 px-3">
                              <span className={`text-xs px-2 py-1 rounded ${
                                regime.type === 'Meublé' ? 'bg-blue-100 text-blue-700' : 
                                regime.type === 'Nu' ? 'bg-amber-100 text-amber-700' : 'bg-purple-100 text-purple-700'
                              }`}>
                                {regime.type}
                              </span>
                            </td>
                            <td className="text-right py-4 px-3 font-mono">
                              {formatCurrency(regime.revenuImposable)}
                            </td>
                            <td className="text-right py-4 px-3 font-mono text-red-600">
                              {formatCurrency(regime.impots)}
                            </td>
                            <td className={`text-right py-4 px-3 font-mono font-semibold ${
                              regime.cashFlowNet >= 0 ? 'text-[#00B050]' : 'text-red-600'
                            }`}>
                              {regime.cashFlowNet >= 0 ? '+' : ''}{formatCurrency(regime.cashFlowNet)}
                            </td>
                            <td className={`text-right py-4 px-3 font-mono font-semibold ${
                              regime.rendementNet >= 0 ? 'text-[#00B050]' : 'text-red-600'
                            }`}>
                              {formatPercent(regime.rendementNet)}
                            </td>
                          </tr>
                        ));
                      })()}
                    </tbody>
                  </table>
                </div>

                {/* Légende */}
                <div className="mt-6 flex flex-wrap gap-4 text-sm">
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 bg-green-50 border border-green-200 rounded"></div>
                    <span className="text-slate-600">Régime optimal pour votre situation</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-4 h-4 ring-2 ring-[#0D47A1] rounded"></div>
                    <span className="text-slate-600">Régime actuellement sélectionné</span>
                  </div>
                </div>

                {/* Note explicative */}
                <div className="mt-6 p-4 bg-blue-50 rounded-lg">
                  <div className="flex items-start gap-3">
                    <Info className="h-5 w-5 text-[#0D47A1] flex-shrink-0 mt-0.5" />
                    <div className="text-sm text-slate-700">
                      <p className="font-medium mb-1">Comment lire ce tableau ?</p>
                      <ul className="list-disc list-inside space-y-1 text-slate-600">
                        <li><strong>Micro-BIC/Micro-Foncier</strong> : Régimes simplifiés avec abattement forfaitaire</li>
                        <li><strong>LMNP/LMP</strong> : Permettent l'amortissement du bien (avantage fiscal majeur)</li>
                        <li><strong>SCI IS</strong> : Société taxée à 15% puis 25% sur les bénéfices</li>
                        <li><strong>Réel</strong> : Déduction des charges réelles (intérêts, travaux, charges)</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Résumé de la situation actuelle */}
            <Card className="border-0 shadow-md bg-gradient-to-r from-[#0D47A1] to-[#1565C0] text-white">
              <CardContent className="p-6">
                <h3 className="text-lg font-bold mb-4">Votre situation actuelle</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-white/10 rounded-lg p-3 text-center">
                    <p className="text-blue-200 text-xs uppercase">Investissement</p>
                    <p className="text-xl font-bold">{formatCurrency(totalInvestissement)}</p>
                  </div>
                  <div className="bg-white/10 rounded-lg p-3 text-center">
                    <p className="text-blue-200 text-xs uppercase">Loyers annuels</p>
                    <p className="text-xl font-bold">{formatCurrency(loyersAnnuels)}</p>
                  </div>
                  <div className="bg-white/10 rounded-lg p-3 text-center">
                    <p className="text-blue-200 text-xs uppercase">TMI</p>
                    <p className="text-xl font-bold">{inputs.tmi}%</p>
                  </div>
                  <div className="bg-white/10 rounded-lg p-3 text-center">
                    <p className="text-blue-200 text-xs uppercase">Type</p>
                    <p className="text-xl font-bold">{inputs.type_location === 'meuble' ? 'Meublé' : 'Nu'}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {activeTab === 'guide' && (
          <div className="space-y-6">
            <Card className="border-0 shadow-md">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <Save className="h-5 w-5 text-[#0D47A1]" />
                    Mes Calculs de Rentabilit&eacute;
                  </CardTitle>
                  {results && (
                    <Button
                      onClick={async () => {
                        try {
                          await axios.post(`${API}/calculator/save`, {
                            inputs,
                            results,
                            name: inputs.project_name || `Calcul du ${new Date().toLocaleDateString('fr-FR')}`
                          }, { withCredentials: true });
                          toast.success('Calcul sauvegard\u00e9 !');
                          // Reload saved calcs
                          const resp = await axios.get(`${API}/calculator/history`, { withCredentials: true });
                          setSavedCalcs(resp.data);
                        } catch {
                          toast.error('Erreur lors de la sauvegarde');
                        }
                      }}
                      className="bg-[#0D47A1] hover:bg-[#0A367A]"
                      data-testid="save-calc-btn"
                    >
                      <Save className="h-4 w-4 mr-2" />
                      Sauvegarder le calcul actuel
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {savedCalcs.length === 0 ? (
                  <div className="text-center py-12">
                    <Calculator className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-500 text-lg mb-2">Aucun calcul sauvegard&eacute;</p>
                    <p className="text-slate-400 text-sm">Remplissez le calculateur puis cliquez sur "Sauvegarder" pour retrouver vos projets ici</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {savedCalcs.map((calc, idx) => (
                      <div
                        key={calc.calc_id || idx}
                        className="border rounded-lg p-4 hover:bg-slate-50 transition-colors group"
                        data-testid={`saved-calc-${idx}`}
                      >
                        <div className="flex items-center justify-between">
                          <div
                            className="flex-1 cursor-pointer"
                            onClick={() => {
                              if (calc.inputs) {
                                setInputs(prev => ({ ...prev, ...calc.inputs }));
                                if (calc.results) setResults(calc.results);
                                setActiveTab('calculator');
                                toast.success('Calcul charg\u00e9 !');
                              }
                            }}
                          >
                            <h4 className="font-semibold text-slate-800">{calc.name || 'Sans nom'}</h4>
                            <p className="text-sm text-slate-500">
                              {calc.created_at ? new Date(calc.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' }) : ''}
                            </p>
                          </div>
                          <div className="flex items-center gap-4 text-right">
                            <div>
                              <p className="text-xs text-slate-500">Renta. brute</p>
                              <p className="font-bold text-[#0D47A1]">{calc.results?.rendement_brut?.toFixed(2) || '0'}%</p>
                            </div>
                            <div>
                              <p className="text-xs text-slate-500">Cash-flow</p>
                              <p className={`font-bold ${(calc.results?.cash_flow_mensuel || 0) >= 0 ? 'text-[#00B050]' : 'text-red-500'}`}>
                                {formatCurrency(calc.results?.cash_flow_mensuel || 0)}
                              </p>
                            </div>
                            <div>
                              <p className="text-xs text-slate-500">Prix</p>
                              <p className="font-medium">{formatCurrency(calc.inputs?.prix_achat || 0)}</p>
                            </div>
                            <Button
                              variant="ghost"
                              size="icon"
                              className="text-slate-400 hover:text-red-500 hover:bg-red-50 opacity-0 group-hover:opacity-100 transition-opacity"
                              data-testid={`delete-calc-${idx}`}
                              onClick={async (e) => {
                                e.stopPropagation();
                                if (!window.confirm('Supprimer ce calcul ?')) return;
                                try {
                                  await axios.delete(`${API}/calculator/history/${calc.calc_id}`, { withCredentials: true });
                                  setSavedCalcs(prev => prev.filter(c => c.calc_id !== calc.calc_id));
                                  toast.success('Calcul supprim\u00e9');
                                } catch {
                                  toast.error('Erreur lors de la suppression');
                                }
                              }}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}
      </main>

      {/* Footer */}
      <footer className="bg-slate-900 text-white py-6 mt-12">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <p className="text-slate-400">Créé par <a href="https://www.webisula.com/" target="_blank" rel="noopener noreferrer" className="text-[#00B050] hover:text-white font-semibold transition-colors">Webisula</a></p>
          <p className="text-sm text-slate-500 mt-1">Rejoignez le club 10 Millions Investor</p>
        </div>
      </footer>
    </div>
  );
};

export default CalculatorPage;
